/**
 * §P.12 · La ficha del buscador.
 *
 * ## Guardar y publicar son dos decisiones, y la pantalla no las junta
 *
 * El contrato lo dice del propio `PUT`: «no publica». La biografía del maestro se
 * escribe en tres tardes, y un botón que guardara y publicara a la vez sacaría a la
 * calle un borrador a medias la primera tarde. Son dos botones y dicen dos cosas
 * distintas.
 *
 * ## «No lo sé» no es «no lo tiene»
 *
 * Las cuatro preguntas de instalaciones —aparcamiento, accesible, vestuarios,
 * profesora— admiten nulo **a propósito**, y por eso van en un desplegable de tres
 * opciones y no en una casilla. Una casilla no sabe decir «no lo sé»: la deja sin
 * marcar, y sin marcar significa «no». Con eso, una ficha sembrada del censo
 * federativo saldría en el buscador afirmando que el club no tiene vestuarios,
 * cuando lo cierto es que nadie se lo ha preguntado.
 *
 * Y no es un matiz de purista: quien filtra por «accesible» descarta lo que dice que
 * no, y castigaría al club por no haber rellenado la ficha.
 *
 * ## El estado que se pinta es `is_listed`, no `published_at`
 *
 * No son lo mismo y el contrato avisa. Una ficha publicada y luego retirada conserva
 * la fecha —es su historia— y deja de salir; deducirlo cruzando las dos columnas es
 * exactamente cómo se acaba pintando «publicada» en verde sobre una ficha que no
 * sale. Viene resuelto del servidor y aquí solo se lee.
 *
 * ## Retirar es del dueño, y tiene que ser tan fácil como publicar
 *
 * `BFF_BO.md` §7.6: «la retirada tiene que ser tan fácil como publicar». Así que el
 * botón está a la vista y no detrás de una confirmación disuasoria — una sí, porque
 * cierra la puerta por la que entran alumnos nuevos, pero una que explique, no una
 * que desanime. Al administrativo no se le enseña: la ruta declara `own`.
 *
 * ## La foto del local, y una regla que no se negocia
 *
 * Es **del tatami vacío**. Nunca de una clase con alumnos dentro: la regla vive en
 * `CLAUDE.md` y es la misma que hace que no exista galería en la app de las familias.
 * Aquí no hay forma de comprobarlo —una foto es una foto— así que se dice justo donde
 * alguien está a punto de elegir el fichero, que es el único momento en que sirve.
 *
 * Se guarda sola en cuanto se sube, sin pasar por «Guardar»: el `PUT` manda el
 * documento entero, así que arrastra lo que haya en pantalla. Ver `ponerFoto`.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import {
  aProblema,
  ErrorDeApi,
  noHayNada,
  type Problema,
} from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fecha } from '@/shared/ui/formato';
import { SubirFichero } from '@/shared/ui/SubirFichero';
import { useImagen } from '@/shared/api/imagenes';
import type { FichaDelDirectorio } from '@/club/directorio/tipos';

/** Las que se hablan en un tatami español. Se guardan como código. */
const IDIOMAS = [
  { code: 'es', nombre: 'Español' },
  { code: 'en', nombre: 'Inglés' },
  { code: 'gl', nombre: 'Gallego' },
  { code: 'ca', nombre: 'Catalán' },
  { code: 'eu', nombre: 'Euskera' },
  { code: 'fr', nombre: 'Francés' },
] as const;

interface Campos {
  headline: string;
  description: string;
  master_bio: string;
  master_rank: string;
  years_active: string;
  accepts_trial: boolean;
  has_parking: string;
  is_accessible: string;
  has_changing_rooms: string;
  female_instructor: string;
  languages: string[];
  precio: string;
}

/** `'si' | 'no' | ''` en la pantalla; `true | false | null` en la API. */
const triATexto = (v: boolean | null): string =>
  v === null ? '' : v ? 'si' : 'no';

const textoATri = (v: string): boolean | null => (v === '' ? null : v === 'si');

const desde = (f: FichaDelDirectorio): Campos => ({
  headline: f.headline ?? '',
  description: f.description ?? '',
  master_bio: f.master_bio ?? '',
  master_rank: f.master_rank ?? '',
  years_active: f.years_active === null ? '' : String(f.years_active),
  accepts_trial: f.accepts_trial,
  has_parking: triATexto(f.has_parking),
  is_accessible: triATexto(f.is_accessible),
  has_changing_rooms: triATexto(f.has_changing_rooms),
  female_instructor: triATexto(f.female_instructor),
  languages: [...f.languages],
  precio: f.price_from_cents === null ? '' : String(f.price_from_cents / 100),
});

/** Una ficha que no existe todavía: el club nunca ha entrado aquí. */
const VACIA: Campos = {
  headline: '',
  description: '',
  master_bio: '',
  master_rank: '',
  years_active: '',
  accepts_trial: true,
  has_parking: '',
  is_accessible: '',
  has_changing_rooms: '',
  female_instructor: '',
  languages: ['es'],
  precio: '',
};

const oNulo = (v: string): string | null => (v.trim() === '' ? null : v.trim());

const enteroONulo = (v: string): number | null => {
  const n = Number(v.trim());
  return v.trim() === '' || !Number.isFinite(n) ? null : Math.round(n);
};

/**
 * El cuerpo del `PUT`, **foto incluida siempre**.
 *
 * El `PUT` de la ficha es de documento entero, y un `cover_asset_id` ausente es
 * «sin foto». «Guardar» lo mandaba sin ella, así que subir la foto y guardar
 * después el texto la borraba. Se vio el 24-09-2026: el fichero subido y
 * confirmado, y la ficha apuntando a ninguno. Con un solo sitio que arma el
 * cuerpo, los dos envíos no pueden volver a diferir.
 */
function cuerpoDeLaFicha(c: Campos, foto: string | null) {
  return {
    headline: oNulo(c.headline),
    description: oNulo(c.description),
    master_bio: oNulo(c.master_bio),
    master_rank: oNulo(c.master_rank),
    years_active: enteroONulo(c.years_active),
    accepts_trial: c.accepts_trial,
    has_parking: textoATri(c.has_parking),
    is_accessible: textoATri(c.is_accessible),
    has_changing_rooms: textoATri(c.has_changing_rooms),
    female_instructor: textoATri(c.female_instructor),
    languages: c.languages,
    // En céntimos, como todo el dinero del sistema. La pantalla habla en euros.
    price_from_cents:
      c.precio.trim() === ''
        ? null
        : Math.round(Number(c.precio.replace(',', '.')) * 100),
    cover_asset_id: foto,
  };
}

export function FichaPublica({
  club,
  puedeRetirar,
}: {
  club: string;
  /** Solo el dueño: la ruta de `unpublish` declara `own`. */
  puedeRetirar: boolean;
}) {
  const cache = useQueryClient();
  const [campos, setCampos] = useState<Campos | null>(null);
  const [sembradoDe, setSembradoDe] = useState<string | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [guardado, setGuardado] = useState(false);
  const [retirando, setRetirando] = useState(false);

  const ficha = useQuery({
    queryKey: ['ficha-publica', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).profile.get();
      /*
       * El `404` es un dato, no un fallo: significa que el club nunca ha escrito su
       * ficha. Se abre el formulario vacío, y el `PUT` la crea — es un `upsert` en
       * el servidor, así que no hace falta un «crear» aparte.
       */
      if (error) {
        const p = aProblema(error);
        if (noHayNada(p)) return null;
        throw new ErrorDeApi(p);
      }
      return data as unknown as FichaDelDirectorio;
    },
  });

  const foto = useImagen(ficha.data?.cover_asset_id);

  // Se siembra durante el render, no en un efecto: un `setState` en un efecto
  // pinta una vez con el formulario vacío. Mismo patrón que el resto del panel.
  if (!ficha.isPending && sembradoDe !== club) {
    setSembradoDe(club);
    setCampos(ficha.data ? desde(ficha.data) : { ...VACIA });
  }

  const refrescar = () => {
    void cache.invalidateQueries({ queryKey: ['ficha-publica', club] });
  };

  const guardar = useMutation({
    mutationFn: async (c: Campos) => {
      const r = await api.v1
        .clubs({ club })
        .profile.put(cuerpoDeLaFicha(c, ficha.data?.cover_asset_id ?? null));
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as FichaDelDirectorio;
    },
    onSuccess: () => {
      setGuardado(true);
      refrescar();
    },
    onError: (e) => {
      setGuardado(false);
      setProblema(aProblema(e));
    },
  });

  /**
   * Guarda **solo la foto**, sin perder lo que hay escrito en pantalla.
   *
   * El `PUT` de la ficha es de documento entero —así lo define el contrato—, así que
   * mandar solo `cover_asset_id` borraría todo lo demás. Se manda lo que la persona
   * tiene delante junto con la foto nueva, que es lo que espera que se guarde.
   */
  const ponerFoto = useMutation({
    mutationFn: async (assetId: string) => {
      const r = await api.v1
        .clubs({ club })
        .profile.put(cuerpoDeLaFicha(campos ?? VACIA, assetId));
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as FichaDelDirectorio;
    },
    onSuccess: () => {
      setGuardado(true);
      refrescar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const cambiarVisibilidad = useMutation({
    mutationFn: async (accion: 'publish' | 'unpublish') => {
      const r =
        accion === 'publish'
          ? await api.v1.clubs({ club }).profile.publish.post()
          : await api.v1.clubs({ club }).profile.unpublish.post();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as FichaDelDirectorio;
    },
    onSuccess: () => {
      setRetirando(false);
      setProblema(null);
      refrescar();
    },
    onError: (e) => {
      setRetirando(false);
      setProblema(aProblema(e));
    },
  });

  if (ficha.isPending || !campos) {
    return <Cargando filas={6} etiqueta="Cargando la ficha" />;
  }

  if (ficha.isError) {
    return (
      <PanelError
        problema={aProblema(ficha.error)}
        reintentar={() => void ficha.refetch()}
      />
    );
  }

  const f = ficha.data;
  const sale = f?.is_listed === true;

  const cambiar = <K extends keyof Campos>(campo: K, valor: Campos[K]) => {
    setCampos((c) => (c ? { ...c, [campo]: valor } : c));
    setGuardado(false);
  };

  /*
   * La comprobación que hace el servidor, dicha antes y **sin deshabilitar nada**:
   * `422 profile_too_empty_to_publish`. Es un aviso porque la regla vive allí, y
   * duplicarla aquí como botón gris sería tener la misma decisión en dos sitios.
   */
  const demasiadoVacia =
    campos.headline.trim() === '' || campos.description.trim() === '';

  const trabajando = guardar.isPending || cambiarVisibilidad.isPending;

  return (
    <section className="formulario">
      <h1>Ficha del buscador</h1>

      <p className="tenue">
        Lo que ve una familia que busca club en{' '}
        <strong>kiapclubs.com/clubes</strong>. No es tu web: es la ficha del
        directorio, y el orden en que sale no se compra.
      </p>

      {problema && <PanelError problema={problema} />}

      {/* ── Estado, y lo primero que se viene a mirar ─────────────────────── */}
      <div className={sale ? 'nota nota-bien' : 'nota'}>
        <p>
          <strong>
            {sale ? 'Tu ficha sale en el buscador.' : 'Tu ficha no sale.'}
          </strong>{' '}
          {/*
            El `slug` sale de la propia ficha y no de la cabecera del panel: es el
            mismo dato en los dos sitios, y pedirlo dos veces es la manera de que un
            día digan cosas distintas.
          */}
          {sale && f ? (
            <>
              Está en{' '}
              <a
                href={`https://kiapclubs.com/clubes/${f.slug}`}
                target="_blank"
                rel="noreferrer"
              >
                kiapclubs.com/clubes/{f.slug}
              </a>
              .
            </>
          ) : (
            'Nadie te encuentra desde el directorio mientras esté así.'
          )}
          {f?.published_at && (
            <>
              {' '}
              {/*
                La fecha es la de la **primera** vez que salió y no se reescribe al
                republicar: un club que lleva dos años en el buscador no empezó hoy.
              */}
              Se publicó por primera vez el {fecha(f.published_at)}.
            </>
          )}
        </p>

        <div className="acciones">
          {!sale && (
            <button
              type="button"
              className="btn-primario"
              disabled={trabajando}
              onClick={() => {
                setProblema(null);
                cambiarVisibilidad.mutate('publish');
              }}
            >
              {f?.published_at ? 'Volver a publicarla' : 'Publicarla'}
            </button>
          )}

          {sale && puedeRetirar && !retirando && (
            <button
              type="button"
              className="btn-plano"
              onClick={() => setRetirando(true)}
            >
              Retirarla del buscador
            </button>
          )}
        </div>

        {/*
          La confirmación explica, no disuade. Retirar tiene que ser tan fácil como
          publicar: lo que no puede es pasar de un clic despistado.
        */}
      </div>

      {retirando && (
        <div className="hueco hueco-confirmar">
          <h3>¿Retirar la ficha del buscador?</h3>
          <ul className="lista-clara">
            <li>
              <strong>Deja de salir ahora mismo.</strong> Nadie te encuentra
              desde el directorio ni te puede reservar una clase de prueba.
            </li>
            <li>
              <strong>Lo escrito se guarda.</strong> Puedes volver a publicarla
              cuando quieras y aparece igual que estaba.
            </li>
          </ul>

          <div className="hueco-acciones">
            <button
              type="button"
              className="btn-peligro"
              disabled={trabajando}
              onClick={() => {
                setProblema(null);
                cambiarVisibilidad.mutate('unpublish');
              }}
            >
              {cambiarVisibilidad.isPending ? 'Retirando…' : 'Sí, retirarla'}
            </button>
            <button
              type="button"
              className="btn-plano"
              onClick={() => setRetirando(false)}
            >
              Dejarla como está
            </button>
          </div>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setProblema(null);
          guardar.mutate(campos);
        }}
      >
        <h2 className="seccion">Lo que se lee primero</h2>

        <div className="rejilla">
          <label>
            Titular
            <input
              maxLength={200}
              placeholder="TaeKwonDo para niños y adultos en Alcalá"
              value={campos.headline}
              onChange={(e) => cambiar('headline', e.target.value)}
            />
          </label>
        </div>

        <label>
          Descripción
          <textarea
            rows={4}
            maxLength={4000}
            placeholder="Qué vais a encontrar aquí y qué os diferencia. Tres o cuatro líneas."
            value={campos.description}
            onChange={(e) => cambiar('description', e.target.value)}
          />
        </label>

        {demasiadoVacia && (
          <p className="nota">
            <strong>Sin titular y sin descripción no se puede publicar.</strong>{' '}
            Una madre que abre el buscador y encuentra un club sin una línea que
            lo explique pasa al siguiente, y esa primera impresión no se repite.
          </p>
        )}

        <h2 className="seccion">El maestro</h2>

        <div className="rejilla">
          <label>
            Grado
            <input
              maxLength={40}
              placeholder="5º Dan"
              value={campos.master_rank}
              onChange={(e) => cambiar('master_rank', e.target.value)}
            />
          </label>

          <label>
            Años dando clase
            <input
              type="number"
              min={0}
              max={120}
              value={campos.years_active}
              onChange={(e) => cambiar('years_active', e.target.value)}
            />
          </label>
        </div>

        <label>
          Biografía
          <textarea
            rows={4}
            maxLength={4000}
            placeholder="Dónde se formó, qué grados tiene, cuánto lleva enseñando."
            value={campos.master_bio}
            onChange={(e) => cambiar('master_bio', e.target.value)}
          />
        </label>

        <h2 className="seccion">El local</h2>

        <p className="tenue">
          Si no lo sabes, déjalo en <strong>«No lo sé»</strong>. No es lo mismo
          que decir que no: quien busca un local accesible descarta los que
          dicen que no lo son, y no queremos descartarte por una pregunta sin
          contestar.
        </p>

        <div className="rejilla">
          {(
            [
              ['has_parking', 'Aparcamiento cerca'],
              ['is_accessible', 'Accesible'],
              ['has_changing_rooms', 'Vestuarios'],
              ['female_instructor', 'Hay profesora'],
            ] as const
          ).map(([campo, etiqueta]) => (
            <label key={campo}>
              {etiqueta}
              <select
                value={campos[campo]}
                onChange={(e) => cambiar(campo, e.target.value)}
              >
                <option value="">No lo sé</option>
                <option value="si">Sí</option>
                <option value="no">No</option>
              </select>
            </label>
          ))}
        </div>

        {/*
          Parece el campo menor de la pantalla y es la mitad de la decisión: una
          madre que busca clases para su hija de siete años filtra por esto antes
          que por precio. Va dicho para que nadie lo deje sin contestar por
          parecer poco importante.
        */}
        <p className="tenue">
          «Hay profesora» se usa más de lo que parece: es uno de los primeros
          filtros de quien busca clases para una niña.
        </p>

        <h2 className="seccion">Precio e idiomas</h2>

        <div className="rejilla">
          <label>
            Desde, al mes
            <input
              type="number"
              min={0}
              step="0.01"
              placeholder="45"
              value={campos.precio}
              onChange={(e) => cambiar('precio', e.target.value)}
            />
          </label>
        </div>

        <p className="etiqueta-suelta">Idiomas en los que se da clase</p>
        <div className="rejilla">
          {IDIOMAS.map((i) => (
            <label key={i.code} className="opcion">
              <input
                type="checkbox"
                checked={campos.languages.includes(i.code)}
                onChange={(e) =>
                  cambiar(
                    'languages',
                    e.target.checked
                      ? [...campos.languages, i.code]
                      : campos.languages.filter((x) => x !== i.code)
                  )
                }
              />
              {i.nombre}
            </label>
          ))}
        </div>

        <h2 className="seccion">Clase de prueba</h2>

        <div className="rejilla">
          <label className="opcion">
            <input
              type="checkbox"
              checked={campos.accepts_trial}
              onChange={(e) => cambiar('accepts_trial', e.target.checked)}
            />
            Acepto reservas de clase de prueba desde el buscador
          </label>
        </div>

        <p className="tenue">
          Es por donde entra la gente. Si lo quitas, tu ficha sigue saliendo
          pero sin el botón de reservar, y quien te encuentre tendrá que
          buscarte el teléfono.
        </p>

        <h2 className="seccion">La foto</h2>

        <p className="tenue">
          Una del tatami vacío:{' '}
          <strong>nunca de una clase con alumnos dentro.</strong> Es la misma
          decisión que hace que en la app de las familias no haya galería, y de
          la que estamos más seguros de todo el producto.
        </p>

        {foto ? (
          <div className="vista-previa vista-previa-foto">
            <img src={foto} alt="La foto puesta en la ficha" />
          </div>
        ) : (
          f?.cover_asset_id && <p className="tenue">Hay una puesta.</p>
        )}

        <SubirFichero
          uso="cover"
          club={club}
          etiqueta={f?.cover_asset_id ? 'Cambiar la foto' : 'Subir una foto'}
          alSubir={(asset) => ponerFoto.mutate(asset.id)}
        />

        <div className="acciones">
          <button type="submit" className="btn-primario" disabled={trabajando}>
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </button>

          {guardado && (
            <span className="pastilla pastilla-bien">
              Guardado{sale ? ' y actualizado en el buscador' : ''}
            </span>
          )}
        </div>

        {/*
          Se dice al lado del botón, no en una ayuda: guardar y publicar son dos
          decisiones y la pantalla no las junta.
        */}
        {!sale && (
          <p className="tenue">
            Guardar no publica. Escríbela con calma y publícala cuando esté.
          </p>
        )}
      </form>
    </section>
  );
}
